import React from "react";
import FormControl from "@mui/material/FormControl";
import MuiSelect from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import { FormError } from "./error";

// Replaces the old antd-based Select. The prop contract is unchanged:
//  - `options` is [{ value, label, disabled? }]; option values may be strings,
//    numbers or booleans and are handed back with their original type.
//  - `onChange(value, option)` receives the raw option value (antd style), not
//    a DOM event, so react-hook-form's `field.onChange` stores the value itself.
//  - When `field` is passed it is spread last (as before), so `field.value`,
//    `field.onChange`, `field.onBlur`, `field.name` and `field.ref` win over the
//    same-named props.
//  - null/undefined/"" values show the placeholder.
const isEmptyValue = (value) =>
  value === undefined || value === null || value === "";

const Select = ({ field, errors, options, ...restProps }) => {
  const {
    value,
    onChange,
    onBlur,
    name,
    ref,
    id,
    placeholder,
    disabled,
    className,
    style,
    // antd-only props that have no MUI equivalent here.
    suffixIcon,
    getPopupContainer,
    notFoundContent,
    filterOption,
    showSearch,
    label,
    ...otherProps
  } = { ...restProps, ...(field || {}) };

  const optionList = Array.isArray(options) ? options : [];
  const findOption = (val) =>
    optionList.find((option) => String(option.value) === String(val));

  const handleChange = (event) => {
    const selected = event.target.value;
    if (onChange) onChange(selected, findOption(selected));
  };

  const renderValue = (selected) => {
    if (isEmptyValue(selected)) {
      return (
        <Box component="span" sx={{ color: "text.disabled" }}>
          {placeholder}
        </Box>
      );
    }
    const option = findOption(selected);
    return option ? option.label : String(selected);
  };

  return (
    <>
      <FormControl
        fullWidth
        size="small"
        disabled={disabled}
        className={className}
        style={style}
      >
        <MuiSelect
          {...otherProps}
          id={id}
          name={name}
          value={isEmptyValue(value) ? "" : value}
          onChange={handleChange}
          onBlur={onBlur}
          inputRef={ref}
          displayEmpty
          renderValue={renderValue}
          inputProps={{
            "aria-label": placeholder || name,
          }}
        >
          {optionList.length > 0 ? (
            optionList.map(({ value: optionValue, label: optionLabel, disabled: optionDisabled }) => (
              <MenuItem
                key={`${optionLabel}${optionValue}`}
                value={optionValue}
                disabled={optionDisabled}
              >
                {optionLabel}
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled value="__select_not_found__">
              {Array.isArray(options) ? "No data" : "Loading..."}
            </MenuItem>
          )}
        </MuiSelect>
      </FormControl>
      {field?.name && <FormError name={field.name} errors={errors} />}
    </>
  );
};

export default React.memo(Select);
